import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { Button, Popover, Tooltip } from "@heroui/react";
import { Check, SlidersHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Timeframe } from "@/types";

const TF_LABEL: Partial<Record<Timeframe, string>> = { "1d": "1D", "1w": "1W" };

/**
 * Timeframes as plain text tabs (Binance's "15m 1h 4h 1D"): the active one
 * bold with a bar under it. Arrow keys move between them.
 */
export function TimeframeTabs({
  value, options, onChange,
}: { value: Timeframe; options: Timeframe[]; onChange: (tf: Timeframe) => void }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: KeyboardEvent, i: number) => {
    const dir = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!dir) return;
    e.preventDefault();
    const next = (i + dir + options.length) % options.length;
    onChange(options[next]!);
    refs.current[next]?.focus();
  };
  return (
    <div role="radiogroup" aria-label="Timeframe" className="flex items-center">
      {options.map((tf, i) => {
        const on = tf === value;
        return (
          <button
            key={tf}
            ref={el => { refs.current[i] = el; }}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(tf)}
            onKeyDown={e => onKey(e, i)}
            className={cn(
              "num relative h-11 px-2.5 text-[13px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus",
              on
                ? "tab-bar font-semibold text-foreground"
                : "text-muted hover:text-foreground",
            )}
          >
            {TF_LABEL[tf] ?? tf}
          </button>
        );
      })}
    </div>
  );
}

export interface IndicatorChip {
  id: string;
  label: string;
  on: boolean;
  onChange: (on: boolean) => void;
  /** Greyed out, with the reason as its tooltip. */
  disabledReason?: string;
}

export interface IndicatorGroup {
  title: string;
  chips: IndicatorChip[];
  /** One plain line on what the chips are. */
  hint: string;
}

function Chip({ c }: { c: IndicatorChip }) {
  const disabled = c.disabledReason !== undefined;
  return (
    <button
      type="button"
      aria-pressed={c.on}
      disabled={disabled}
      title={c.disabledReason}
      onClick={() => c.onChange(!c.on)}
      className={cn(
        "relative flex h-8 items-center rounded-lg border px-3 text-xs outline-none transition-colors focus-visible:ring-2 focus-visible:ring-focus",
        c.on ? "border-foreground font-semibold text-foreground" : "border-border text-muted hover:text-foreground",
        disabled && "cursor-not-allowed opacity-40 hover:text-muted",
      )}
    >
      {c.label}
      {c.on && (
        <span className="absolute -right-1 -top-1 flex size-3.5 items-center justify-center rounded-full bg-foreground text-surface">
          <Check className="size-2.5" strokeWidth={3} />
        </span>
      )}
    </button>
  );
}

/** One "Indicators" button with every chart overlay as a chip (Binance's indicator sheet). */
export function IndicatorsMenu({ groups }: { groups: IndicatorGroup[] }) {
  const onCount = groups.reduce((n, g) => n + g.chips.filter(c => c.on).length, 0);
  return (
    <Popover>
      <Button size="sm" variant="ghost" aria-label={`Indicators, ${onCount} on`} className="gap-1.5 px-2 text-muted hover:text-foreground">
        <SlidersHorizontal className="size-4" />
        <span className="hidden text-xs @lg:inline">Indicators</span>
      </Button>
      <Popover.Content placement="bottom end" className="w-80">
        <Popover.Dialog className="space-y-4 p-4" aria-label="Indicators">
          <p className="text-sm font-semibold">Indicators</p>
          {groups.map(g => (
            <section key={g.title} className="space-y-2">
              <p className="text-2xs font-medium uppercase tracking-wide text-muted">{g.title}</p>
              <div className="flex flex-wrap gap-2">
                {g.chips.map(c => <Chip key={c.id} c={c} />)}
              </div>
              <p className="text-xs leading-relaxed text-muted">{g.hint}</p>
            </section>
          ))}
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

/** A quiet icon button for the chart toolbar. */
export function ToolbarIcon({
  label, active, onPress, children,
}: { label: string; active?: boolean; onPress: () => void; children: ReactNode }) {
  return (
    <Tooltip delay={300}>
      <Button
        isIconOnly
        size="sm"
        variant="ghost"
        aria-label={label}
        aria-pressed={active}
        onPress={onPress}
        className={cn("text-muted hover:text-foreground", active && "bg-default text-foreground")}
      >
        {children}
      </Button>
      <Tooltip.Content>{label}</Tooltip.Content>
    </Tooltip>
  );
}
